// @atlas/contracts — a `services` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { INVOICE_DELIVERY_TYPES, INVOICE_ISSUER_KEYS, INVOICE_QUANTITY_UNITS, INVOICE_TYPES, USAGE_REFS_MAX, } from '@atlas/shared';
import { z } from 'zod';
import { AccountIntegrationSchema } from '../accounts/schemas.js';
import { AccountNamedRefSchema, BillingTypeSchema, CurrencySchema, UsageSourceTypeSchema, VatRateSchema, boundedDecimalStr, decimalStr, isoDateStr, nullableIsoDate, nullableStr, toQueryArray, } from '../_shared/schemas.js';
/**
 * Szolgáltatás-katalógus sor (services lista-nézet, belső): a `billing.services` [SHARED] busz-erőforrás
 * fehérlistás mezői (i18n-név a `{hu,en}` JSON-ból feloldva a UI nyelvére a BFF-ben, structureType, status)
 * + a katalógus-csomagok (`billing.service_plans`, `scope='catalog'`) BFF-oldali dúsítása: csomag-szám
 * (`planCount`), a planeken lévő DISTINCT fő-kategóriák (`categories`, a `mainCategory`-ból), és a csomagok
 * árain (`billing.service_plan_prices`) számolt ár-tartomány: a DOMINÁNS deviza (a legtöbb ársávot adó
 * kód) min/max nettó ára, Decimal→string alakban (a pontosság megőrzéséért; a formázás a UI dolga). Ha
 * nincs ár, a `priceMin`/`priceMax`/`priceCurrency` mind null. A `legacyId` sosem jön. Sor-részletnézet nincs.
 */
/** Egy ársor a LISTA árazás-cellájához (a legacy árazás-oszlop 1:1): típus + nettó ár (Decimal-string) + deviza + sáv/blokk. */
export const ServiceCatalogPriceRowSchema = z.object({
    type: z.string(),
    netPrice: z.string(),
    currency: z.string(),
    tierMin: z.number().nullable(),
    tierMax: z.number().nullable(),
    blockSize: z.number().nullable(),
});
/** Egy csomag a LISTA árazás-cellájához: feloldott név + billing/pricing típus + ársorai (a legördülő breakdownhoz). */
export const ServiceCatalogPlanRowSchema = z.object({
    name: z.string(),
    billingType: z.string(),
    pricingType: z.string(),
    prices: z.array(ServiceCatalogPriceRowSchema),
});
export const ServiceCatalogRowSchema = z.object({
    id: z.string(),
    /** A szolgáltatás neve a hívó nyelvén (a `{hu,en}` i18n JSON-ból feloldva; fallback a másik nyelv). */
    name: z.string(),
    /** simple | package (a katalógus szerkezet-típusa). */
    structureType: z.string(),
    /** active | inactive. */
    status: z.string(),
    /** A szolgáltatáshoz tartozó (nem-törölt, katalógus-scope) csomagok száma. */
    planCount: z.number(),
    /** A csomagokon lévő DISTINCT fő-kategóriák (mainCategory: product | advisory | other), betűrendben. */
    categories: z.array(z.string()),
    /** Az ár-tartomány alsó határa a domináns devizában — Decimal→string; nincs ár → null. */
    priceMin: z.string().nullable(),
    /** Az ár-tartomány felső határa a domináns devizában — Decimal→string; nincs ár → null. */
    priceMax: z.string().nullable(),
    /** A domináns deviza (a legtöbb ársávot adó ISO-4217 kód: HUF | EUR | USD); nincs ár → null. */
    priceCurrency: z.string().nullable(),
    /** A TELJES árazás-breakdown a lista árazás-cellájához (csomagonként a billing/pricing típus + ársorok) — a
     *  legacy árazás-oszlop 1:1, legördülő (ExpandableCell) badge-ekkel/árakkal. Üres → nincs csomag/ár. */
    pricing: z.array(ServiceCatalogPlanRowSchema),
    /** A kétnyelvű név/leírás nyers részei + a jegy-dropdown flag — a LISTÁBAN nem jelennek meg, de a
     *  SZERKESZTŐ-form előtöltéséhez kellenek (a `name` a feloldott, megjelenítendő egy-nyelvű változat). */
    nameHu: z.string(),
    nameEn: z.string(),
    descriptionHu: z.string().nullable(),
    descriptionEn: z.string().nullable(),
    showInTicketDropdown: z.boolean(),
    /** Felelős ügyfélszolgálat FK (workforce/support-teams) — a szerkesztő előtöltéséhez; nincs → null. */
    supportTeamId: z.string().nullable(),
    /**
     * A felelős ügyfélszolgálat FELOLDOTT, lokalizált neve — a lista „Felelős ügyfélszolgálat" oszlopához
     * (a legacy `client_support` oszlop 1:1). Nincs hozzárendelés VAGY nem feloldható (törölt csapat /
     * hiányzó `support_teams.view` jog) → null; a UI ilyenkor em-dasht mutat.
     */
    supportTeamName: z.string().nullable(),
    /** OCC-verzió (a busz-sor `version`-je) — a szerkesztés `If-Match`-éhez. */
    version: z.number(),
});
/**
 * Szolgáltatás (Service) create/update közös body — a `billing/services` [SHARED] busz-sor whitelistelt,
 * szerkeszthető mezői SERVICE-szinten (a csomag+árazás KÜLÖN, 2. increment). A `name`/`description` KÉTNYELVŰ
 * (`{hu,en}` JSON — a legacy name_hu/en 1:1). A `structureType` package→simple konverziót a BFF TILTJA (422).
 * A `supportTeamId`/`groupedInCharts`/`partnerTicketExcluded` a form-on kívül (whitelist-merge megőrzi).
 */
export const ServiceWriteBodySchema = z.object({
    name: z.object({
        hu: z.string().trim().min(1).max(255),
        en: z.string().trim().min(1).max(255),
    }),
    description: z
        .object({ hu: z.string().max(500), en: z.string().max(500) })
        .nullable()
        .optional(),
    structureType: z.enum(['simple', 'package']),
    status: z.enum(['active', 'inactive']),
    showInTicketDropdown: z.boolean(),
});
/** Ártétel-típus (a legacy PriceTypeEnum 1:1) — a pricingType→priceType mátrix soraihoz. */
export const PriceTypeSchema = z.enum(['unit', 'base', 'tier', 'overflow', 'passive', 'minimum']);
/** Árazási típus (a legacy PricingTypeEnum 1:1). */
export const PricingTypeSchema = z.enum(['flat', 'tiered', 'base_tiered', 'commission']);
/** Fő-kategória (a legacy ServiceMainCategoryEnum 1:1; default `other`). */
export const MainCategorySchema = z.enum(['product', 'advisory', 'other']);
/** Alkategória (a legacy ServiceSubCategoryEnum 1:1). A főkat→alkat mátrixot lásd `SUBCATEGORIES_BY_MAIN`. */
export const SubCategorySchema = z.enum([
    'roomsome',
    'innovation',
    'digital_marketing',
    'revenue_management',
    'web_development',
    'other',
]);
/**
 * FŐKATEGÓRIA → az engedélyezett ALKATEGÓRIÁK (a legacy `ServiceSubCategoryEnum` mapping 1:1).
 *
 * ITT, a szerződésben él, mert MINDKÉT fél érvényesíti: a web-form a főkat-váltáskor erre resetel és csak ezeket
 * kínálja, a BFF pedig 422-t ad a párosítást sértő bemenetre (a fal a szerveren van — a kliens-oldali szűkítés
 * csak kényelem). Egy másolat elcsúszása némán nonszensz kategória-párt engedne (pl. product/web_development),
 * ami a lista-szűrőkben és a bevétel-előrejelzés bontásaiban is megjelenik.
 *
 * Az első elem egyben a főkat-váltás AUTO-RESET célja (a legacy `updatedMainCategory()`).
 */
export const SUBCATEGORIES_BY_MAIN = {
    product: ['roomsome', 'innovation'],
    advisory: ['digital_marketing', 'revenue_management', 'web_development'],
    other: ['other'],
};
/** Engedélyezett-e a (főkategória, alkategória) páros. */
export function isSubCategoryOfMain(main, sub) {
    return SUBCATEGORIES_BY_MAIN[main].includes(sub);
}
/**
 * Egy ársor (`billing/service-plan-prices`). A `netPrice` DECIMAL-STRING (Decimal(15,2) — soha float/number).
 * Meglévő sor `id`-vel jön (diff/update), új sornál hiányzik. A tier-mezők egészek; a `tierMin` 0-tól, a
 * `tierMax`/`blockSize` pozitív. A pricingType→priceType mátrixot + a tier-monotonitást a BFF érvényesíti (422).
 */
export const ServicePlanPriceSchema = z.object({
    id: z.string().optional(),
    type: PriceTypeSchema,
    netPrice: z
        .string()
        .regex(/^\d+(\.\d{1,2})?$/, 'Érvénytelen nettó ár (nem-negatív, max 2 tizedes).'),
    currency: CurrencySchema,
    tierMin: z.number().int().nonnegative().nullable().optional(),
    tierMax: z.number().int().positive().nullable().optional(),
    blockSize: z.number().int().positive().nullable().optional(),
});
/**
 * Egy csomag (`billing/service-plans`) + ársorai — a szerkesztő nested egysége. Meglévő plan `id`-vel jön
 * (diff), új plannél hiányzik. A `name` kétnyelvű ({hu,en}). `simple` szolgáltatásnál pontosan 1 plan (a neve
 * a szolgáltatás nevéből öröklődik a BFF-ben), `package`-nél N. A `usageSourceType` csak commission/tiered plannél.
 */
export const ServicePlanSchema = z.object({
    id: z.string().optional(),
    name: z.object({
        hu: z.string().trim().min(1).max(255),
        en: z.string().trim().min(1).max(255),
    }),
    mainCategory: MainCategorySchema,
    subCategory: SubCategorySchema,
    billingType: BillingTypeSchema,
    pricingType: PricingTypeSchema,
    usageSourceType: UsageSourceTypeSchema.nullable().optional(),
    vatRate: VatRateSchema,
    /**
     * A számlára kerülő tétel-név — ÍRÁSKOR KÖTELEZŐ (user-döntés). Üresen a hozzárendelés pillanatképe is üres
     * maradt, és a számlára egy azóta átnevezett csomagnév került (élesben 9 csomag, 4 számlázandó sor).
     */
    invoiceItemName: z.string().trim().min(1).max(255),
    invoiceItemNote: z.string().max(1000).nullable().optional(),
    prices: z.array(ServicePlanPriceSchema),
});
/**
 * READ: teljes szolgáltatás a HÁROMSZINTŰ szerkesztőnek (service + plans[]+prices[], OCC-verziókkal). A
 * `version` a service-sor OCC-je (a PUT If-Match-éhez), a planeken külön `version` (a plan-PUT If-Match-éhez).
 * A `name`/`description` kétnyelvű nyers ({hu,en}) — a szerkesztő-form előtöltéséhez. A `legacyId` sosem jön.
 */
export const ServiceCatalogDetailSchema = z.object({
    id: z.string(),
    version: z.number(),
    name: z.object({ hu: z.string(), en: z.string() }),
    description: z.object({ hu: z.string(), en: z.string() }).nullable(),
    structureType: z.enum(['simple', 'package']),
    status: z.enum(['active', 'inactive']),
    showInTicketDropdown: z.boolean(),
    supportTeamId: z.string().nullable(),
    groupedInCharts: z.boolean(),
    // OLVASÁSKOR a számlatétel-név lehet üres (a kötelezőség előtti sorok) — a szerkesztő ilyenkor kitöltést kér
    plans: z.array(ServicePlanSchema.extend({ version: z.number(), invoiceItemName: z.string().nullable() })),
});
/**
 * WRITE: nested create/update body (service + plans[]). A csomagok legalább 1-en (a `simple` → pontosan 1,
 * a `package` → N — a pontos számot + a pricing-mátrixot + tier-monotonitást a BFF érvényesíti, 422). A
 * `structureType` package→simple konverzióját a BFF TILTJA update-en (422). A currency-t a BFF MINDIG írja.
 */
export const ServiceCatalogWriteSchema = z.object({
    name: z.object({
        hu: z.string().trim().min(1).max(255),
        en: z.string().trim().min(1).max(255),
    }),
    description: z
        .object({ hu: z.string().max(500), en: z.string().max(500) })
        .nullable()
        .optional(),
    structureType: z.enum(['simple', 'package']),
    status: z.enum(['active', 'inactive']),
    showInTicketDropdown: z.boolean(),
    /** Felelős ügyfélszolgálat (workforce/support-teams FK) — a legacy „client_support" választó 1:1. A `null`
     *  törli a hozzárendelést. A BFF a service-sorra írja (whitelist-merge); a partnerTicketExcluded marad. */
    supportTeamId: z.string().uuid().nullable().optional(),
    groupedInCharts: z.boolean().optional(),
    plans: z.array(ServicePlanSchema).min(1),
});
/** Szolgáltatás-katalógus lekérdezés: szabad-szöveg (név) + kategória/státusz-szűrő + rendezés + offset-lapozás
 *  (a BFF-szűrt halmazon; a billing.services SHARED tábla nem támogat szerver-oldali szűrést/rendezést). */
export const ServiceCatalogListQuerySchema = z.object({
    /** Szabad-szöveg: a szolgáltatás nevére, a CSOMAG-nevekre (package) ÉS az árakra (netPrice) illeszt. */
    q: z.string().optional(),
    /** Szerkezet-szűrő (simple | package) — a szolgáltatáson. */
    structureType: z.string().optional(),
    /** Fő-kategória-szűrő (product | advisory | other) — a csomagok kategóriáira illeszt. */
    category: z.string().optional(),
    /** Al-kategória-szűrő (roomsome | innovation | digital_marketing | …) — a csomagok alkategóriáira illeszt. */
    subCategory: z.string().optional(),
    /** Számlázási-típus-szűrő (recurring | one_time) — a csomagok billingType-jára illeszt (bármely csomag). */
    billingType: z.string().optional(),
    /** Árazási-típus-szűrő (flat | tiered | base_tiered | commission) — a csomagok pricingType-jára illeszt (bármely csomag). */
    pricingType: z.string().optional(),
    /** Státusz-szűrő (active | inactive). */
    status: z.string().optional(),
    /** Rendezés-kulcs (name | planCount | priceRange; a legacy alap: name). Az ismeretlen kulcs a BFF-ben
     *  name-re esik vissza (a sibling-oldalak string-sort konvenciója). A planCount/priceRange BFF-oldalon számolt. */
    sort: z.string().optional(),
    /** Rendezés iránya (alap: asc). */
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/** Assignment-scope: az ügyfél egésze vagy egy konkrét fiók. */
const SERVICE_ASSIGNMENT_SCOPES = ['client', 'account'];
/** Assignment-típus: `tracking` (sosem számlázott) vagy `billing` (számlázandó). */
const SERVICE_ASSIGNMENT_TYPES = ['tracking', 'billing'];
/** Assignment-státusz (domain, a legacy StatusEnum). */
const SERVICE_ASSIGNMENT_STATUSES = ['active', 'pending', 'inactive'];
/** Számlázási gyakoriság (recurring plan). */
const SERVICE_ASSIGNMENT_BILLING_FREQUENCIES = [
    'monthly',
    'quarterly',
    'semi_annual',
    'annual',
];
/** Számlázási megállapodás: elő- vagy utólagos fizetés. */
const SERVICE_ASSIGNMENT_BILLING_AGREEMENTS = ['prepaid', 'postpaid'];
/** Ársáv-típus (a katalógus- és az assignment-ár közös értékkészlete). */
const SERVICE_PRICE_TYPES = ['unit', 'base', 'tier', 'overflow', 'passive', 'minimum'];
export const ServiceAssignmentScopeSchema = z.enum(SERVICE_ASSIGNMENT_SCOPES);
export const ServiceAssignmentTypeSchema = z.enum(SERVICE_ASSIGNMENT_TYPES);
export const ServiceAssignmentStatusSchema = z.enum(SERVICE_ASSIGNMENT_STATUSES);
export const ServiceAssignmentBillingFrequencySchema = z.enum(SERVICE_ASSIGNMENT_BILLING_FREQUENCIES);
/**
 * Árazó-OVERRIDE blokk a hozzárendeléshez (a Phase-1 katalógus-árazó alak újrahasznosítva — `ServicePlanPriceSchema`
 * + `PricingTypeSchema` + `VatRateSchema`). A `pricingType` a KATALÓGUS-plan típusa VAGY `flat` (lefele-váltás; a
 * felfelé-váltást a BFF 422-vel tiltja — `catalogPricingType` a korlát forrása). A `prices[]` a — esetleg felülírt —
 * ár-sorok; a BFF ezeket írja a `service-plan-assignment-prices`-ba (NEM vak katalógus-másolat), a currency MINDIG
 * kiírva. Ha a body-ból HIÁNYZIK (billing típusnál), a BFF a katalógus árait másolja (visszafelé-kompatibilis).
 */
export const ServiceAssignmentPricingSchema = z.object({
    pricingType: PricingTypeSchema,
    vatRate: VatRateSchema,
    prices: z.array(ServicePlanPriceSchema),
});
/**
 * KOMPONENS-FA egy eleme (komplex/csomag hozzárendelés gyerek-sora — a legacy formComponents[] 1:1). Egy gyerek
 * mindig `tracking` típusú, FIX (flat) árazású `service-plan-assignments` sor `parentId = szülő id`-vel; a
 * `clientId`/`accountId`/`scope`/`status`/szerződés-dátumok a SZÜLŐBŐL öröklődnek (SOHA kliens-inputból). A gyerek
 * saját KATALÓGUS-planjéből épül a `planSnapshot` + egy `unit` ár-sor (`unitPrice`/`currency`). A szülő `unit`
 * alap-ára a gyerekek árainak összege (`Σ unitPrice × quantity`) — szerver-oldalon számítva, NEM kézi ár. Az `id`
 * a MEGLÉVŐ gyerek azonosítója (update-reconcile: id-vel → frissítés, id nélkül → új; a hiányzó → törlés).
 */
export const ServiceAssignmentComponentSchema = z.object({
    id: z.string().uuid().optional(),
    servicePlanId: z.string().uuid(),
    /**
     * KOMPONENS-SZINTŰ kapcsolattartó / szakértő (`service-plan-assignment-users` pivot a GYEREK soron, role
     * `contact`/`expert`) — a legacy `formComponentContactIds[i]` 1:1. Miért kell: a komplex szolgáltatás
     * komponenseit KÜLÖN kollégák viszik, és a legacy adatban ez a többségi eset (a 751 pivot-sorból 412 a
     * gyerekekhez tartozik, 240 komplex hozzárendelésen). A szülő contact/expert-je ettől független.
     */
    contactId: z.string().uuid().nullable().optional(),
    expertId: z.string().uuid().nullable().optional(),
    // Korlátozott tartomány: az egész-rész max 8 számjegy (a busz `Decimal(12,2)` biztonságos alsó része) — így az
    // `unitPrice × quantity` (és a szülő Σ) tiszta 422-vel esik el a bemeneten, NEM 502-vel a busz-írásnál.
    quantity: boundedDecimalStr,
    unitPrice: boundedDecimalStr,
    currency: CurrencySchema,
});
/** A komponens-fa (gyerekek) felső korlátja egy hozzárendelésen — DoS-védő plafon (a legacy nem korlátozott). */
const SERVICE_ASSIGNMENT_MAX_COMPONENTS = 50;
/**
 * A hozzárendelés által FOGYASZTOTT fiók-integráció: melyik fiók melyik azonosító-eleme (a fiók
 * `integrations[].id`-ja). Hivatkozás, nem másolat — a fiókon javított külső azonosítót a kötés követi.
 */
export const UsageRefSchema = z.object({
    accountId: z.string().uuid(),
    integrationId: z.string().uuid(),
});
/**
 * A kötés-lista a write-body-kban: TELJES új állapot (nem patch), egy pár nem állhat kétszer. A BFF ellenőrzi,
 * hogy minden pár az ügyfél fiókjára és annak LÉTEZŐ, a forrás-típushoz illő azonosítójára mutat, és hogy
 * használat-alapú hozzárendelésnél legalább egy kötés van (422 különben).
 */
const UsageRefListSchema = z
    .array(UsageRefSchema)
    .max(USAGE_REFS_MAX)
    .refine((list) => new Set(list.map((r) => `${r.accountId}|${r.integrationId}`)).size === list.length, { message: 'Egy azonosító kétszer szerepel' });
/**
 * Create-body: a törzs-mezők + a KATALÓGUS-plan azonosítója (`servicePlanId`). A `planSnapshot`-ot a BFF a
 * katalógusból építi (a snapshot vatRate/pricingType/invoiceItem* felülírható a `pricing`/`invoiceItem*` mezőkkel).
 * `parentId` NINCS a body-ban (komponens-fa halasztva → mindig root). A cél-tenant a `clientId` (szerver-oldalon
 * feloldva). `pricing` = ár-override; `contactId`/`expertId` → `service-plan-assignment-users` pivot; `accountIds`
 * (client-scope) → `assignment-accounts` pivot.
 */
export const ServiceAssignmentCreateBodySchema = z.object({
    clientId: z.string().uuid(),
    accountId: z.string().uuid().nullable().optional(),
    servicePlanId: z.string().uuid(),
    scope: ServiceAssignmentScopeSchema,
    type: ServiceAssignmentTypeSchema,
    status: ServiceAssignmentStatusSchema.default('active'),
    quantity: decimalStr,
    quantityUnit: z.enum(INVOICE_QUANTITY_UNITS),
    issuerKey: z.enum(INVOICE_ISSUER_KEYS).nullable().optional(),
    invoiceType: z.enum(INVOICE_TYPES),
    invoiceDeliveryType: z.enum(INVOICE_DELIVERY_TYPES),
    billingFrequency: ServiceAssignmentBillingFrequencySchema.nullable().optional(),
    billingAgreement: z.enum(SERVICE_ASSIGNMENT_BILLING_AGREEMENTS).nullable().optional(),
    billingStartDate: nullableIsoDate,
    billingEndDate: nullableIsoDate,
    contractStartDate: nullableIsoDate,
    contractEndDate: nullableIsoDate,
    firstInvoiceProrated: z.boolean().optional(),
    showComponentsOnInvoice: z.boolean().optional(),
    paymentCardId: z.string().uuid().nullable().optional(),
    /** Egyedi számlatétel-név/megjegyzés (a snapshot invoiceItemName/Note felülírása; üres → a katalógus-érték). */
    invoiceItemName: nullableStr(255),
    invoiceItemNote: nullableStr(1000),
    /** Használat-forrás (a snapshot usageSourceType felülírása). Tiered/base_tiered/commission árazásnál a BFF
     *  KÖTELEZŐVÉ teszi (422 hiányában). A tényleges usage-adat-feed NEM ehhez a körhöz tartozik. */
    usageSourceType: UsageSourceTypeSchema.nullable().optional(),
    /** Kapcsolattartó / szakértő identity (pivot role=contact|expert). */
    contactId: z.string().uuid().nullable().optional(),
    expertId: z.string().uuid().nullable().optional(),
    /** Client-scope: több érintett fiók (assignment-accounts pivot). Account-scope-nál figyelmen kívül. */
    accountIds: z.array(z.string().uuid()).optional(),
    /**
     * A MÉRT AZONOSÍTÓK: melyik fiók melyik külső azonosítójának használata számít bele a tételbe. Használat-alapú
     * hozzárendelésnél (van `usageSourceType`) KÖTELEZŐ, legalább egy — a BFF 422-vel utasítja el nélküle.
     */
    usageRefs: UsageRefListSchema.optional(),
    /**
     * MEGOSZTOTT HASZNÁLAT: ha több hozzárendelés ugyanarra a mért azonosítóra köt (egy közös loyalty-program több
     * hotellel), a regisztráltak utáni díjrész a bekapcsolt társak között egyenlően oszlik. CSAK `rs_loyalty`
     * forrásnál engedett (a BFF 422-vel utasítja el máshol).
     */
    splitSharedUsage: z.boolean().optional(),
    /** Ár-override (a katalógus-árak helyett/felett). Hiányzik → billing típusnál a katalógus árai másolódnak. */
    pricing: ServiceAssignmentPricingSchema.optional(),
    /**
     * KOMPONENS-FA (komplex/csomag hozzárendelés): a gyerek-komponensek. Ha megadva (nem-üres), a hozzárendelés
     * KOMPLEX → a szülő árazása KÉNYSZERÍTVE `flat`, az alap-ára a gyerekek árainak ÖSSZEGE (a `pricing` ár-override
     * ilyenkor figyelmen kívül a szülő árára). Minden gyerek AZONOS devizájú kell legyen. A gyerekek `tracking`
     * `parentId`-fás sorok — a `clientId`/`scope`/tulajdonos a SZÜLŐBŐL öröklődik (a busz injektálja a tenantot).
     */
    components: z
        .array(ServiceAssignmentComponentSchema)
        .max(SERVICE_ASSIGNMENT_MAX_COMPONENTS)
        .optional(),
    note: nullableStr(5000),
});
/**
 * Update-body (PARTIAL — a szerkesztés-lock miatt a „mely mező érkezett" számít): a szerkeszthető törzs-mezők,
 * MIND opcionális. A `clientId`/`servicePlanId`/`scope`/`accountId`/`type`/`parentId` NEM módosítható itt (a
 * plan-váltás/scope-váltás delete+recreate). Számlázott assignmentnél a BFF a plan `billingType`-ja szerint
 * szűkíti a megengedett mezőket (one_time → csak `note`; recurring → status, quantity, quantityUnit,
 * issuerKey, billingEndDate, contract-dátumok, note); nem-szerkeszthető mező küldése → 422.
 */
export const ServiceAssignmentUpdateBodySchema = z.object({
    status: ServiceAssignmentStatusSchema.optional(),
    /**
     * A típus (számlázás ↔ nyilvántartás) utólagos váltása — számlázott soron is (a JÖVŐBELI generálásra hat, a
     * kiállított számlákat nem érinti; elírás javítására). Nyilvántartás → számlázás csak ismétlődő díjtípuson,
     * gyakorisággal, kezdő dátummal és árazással (422). Plan-váltásnál a cél-plan dönt, ez nem hat.
     */
    type: ServiceAssignmentTypeSchema.optional(),
    /** Plan-VÁLTÁS (CSAK nem-számlázott assignment): a KATALÓGUS-plan új azonosítója. Ha eltér a jelenlegitől,
     *  a BFF újraépíti a planSnapshot-ot + a service-plan-assignment-prices sorokat (delete-all + recreate).
     *  Számlázottnál a küldése (ha eltér) → 422 (a szerkesztés-lock kiterjesztése; egyező id → no-op). */
    servicePlanId: z.string().uuid().optional(),
    quantity: decimalStr.optional(),
    quantityUnit: z.enum(INVOICE_QUANTITY_UNITS).optional(),
    issuerKey: z.enum(INVOICE_ISSUER_KEYS).nullable().optional(),
    invoiceType: z.enum(INVOICE_TYPES).optional(),
    invoiceDeliveryType: z.enum(INVOICE_DELIVERY_TYPES).optional(),
    billingFrequency: ServiceAssignmentBillingFrequencySchema.nullable().optional(),
    billingAgreement: z.enum(SERVICE_ASSIGNMENT_BILLING_AGREEMENTS).nullable().optional(),
    billingStartDate: nullableIsoDate,
    billingEndDate: nullableIsoDate,
    contractStartDate: nullableIsoDate,
    contractEndDate: nullableIsoDate,
    firstInvoiceProrated: z.boolean().optional(),
    showComponentsOnInvoice: z.boolean().optional(),
    paymentCardId: z.string().uuid().nullable().optional(),
    invoiceItemName: nullableStr(255),
    invoiceItemNote: nullableStr(1000),
    /** Használat-forrás felülírás (snapshot). Számlázottnál zárolt (422); tiered/base_tiered/commission
     *  árazásnál kötelező (422 hiányában). */
    usageSourceType: UsageSourceTypeSchema.nullable().optional(),
    contactId: z.string().uuid().nullable().optional(),
    expertId: z.string().uuid().nullable().optional(),
    accountIds: z.array(z.string().uuid()).optional(),
    /**
     * A mért azonosítók TELJES új listája (hiányzik → érintetlen). Számlázott hozzárendelésen is szerkeszthető:
     * a kötés a JÖVŐBELI számlákra hat, a kiállítottakat nem érinti — és pont a rossz kötés javítása a cél.
     */
    usageRefs: UsageRefListSchema.optional(),
    /** A megosztott használat kapcsolója (hiányzik → érintetlen). Számlázott hozzárendelésen is szerkeszthető — a
     *  JÖVŐBELI számlákra hat. CSAK `rs_loyalty` forrásnál engedett (422). */
    splitSharedUsage: z.boolean().optional(),
    /** Ár-override újraszinkron (delete-all assignment-prices + recreate) — CSAK ha az árazás nem zárolt (nem
     *  számlázott). Számlázott assignmentnél a `pricing` küldése → 422 (a szerkesztés-lock kiterjesztése). */
    pricing: ServiceAssignmentPricingSchema.optional(),
    /**
     * KOMPONENS-FA reconcile (CSAK nem-számlázott — a fa változtatja a szülő árát): a KÍVÁNT gyerek-halmaz. Az `id`-s
     * gyerek frissül, az id-nélküli új, a hiányzó törlődik (delete-all-and-recreate helyett diff). A szülő alap-ára
     * újraszámolódik (`Σ`), pricingType `flat`. Számlázottnál a küldése → 422; bizonytalan számlázottságnál → 409.
     */
    components: z
        .array(ServiceAssignmentComponentSchema)
        .max(SERVICE_ASSIGNMENT_MAX_COMPONENTS)
        .optional(),
    note: nullableStr(5000),
});
/**
 * Lezárás / FELOLDÁS body.
 *
 * - `billingEndDate: 'YYYY-MM-DD'` → **lezárás** (csak `type=billing` + `recurring` plan; egyébként 422),
 * - `billingEndDate: null` → **feloldás**: a záró dátumok törlése. Ez MINDEN root soron megengedett, a
 *   nyilvántartás-típuson is — élesben **385 lezárt nyilvántartás-sor** van (a legacy-ből örökölt záró
 *   dátummal), és ha a feloldás ugyanazt a szűkítést kapná, mint a lezárás, a felület lakatot mutatna
 *   rajtuk, amit senki nem tud kinyitni. A feloldásnak nincs számlázási következménye sem: a generátor
 *   csak `type=billing` sorokat vesz munkába.
 */
export const ServiceAssignmentCloseBodySchema = z.object({
    billingEndDate: isoDateStr.nullable(),
});
/** Egy assignment-ársáv (a `service-plan-assignment-prices` sor tipizálva — detail + refs). */
export const ServiceAssignmentPriceSchema = z.object({
    id: z.string(),
    type: z.enum(SERVICE_PRICE_TYPES),
    tierMin: z.number().nullable(),
    tierMax: z.number().nullable(),
    blockSize: z.number().nullable(),
    netPrice: z.string(),
    currency: z.string(),
});
/**
 * Assignment lista-sor: a törzs-mezők + a feloldott nevek (ügyfél/fiók a foundation-ból, szolgáltatás/plan a
 * `planSnapshot`-ból) + a számított nettó alap-összeg (Σ unit×quantity + Σ base, a domináns devizában).
 */
export const ServiceAssignmentRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    accountId: z.string().nullable(),
    clientName: z.string().nullable(),
    accountName: z.string().nullable(),
    servicePlanId: z.string(),
    serviceName: z.string().nullable(),
    planName: z.string().nullable(),
    /**
     * A SZÁMLATÉTEL MEGJEGYZÉSE a hozzárendelés `planSnapshot`-jából (a hozzárendelés-szintű felülírással együtt).
     *
     * MIÉRT A LISTÁN (user-lelet): az ESETI díj pontos megnevezése CSAK itt áll — a „Setup díj" szolgáltatás
     * „Digital marketing" csomagjának tétel-neve általános („Digital marketing setup díj"), a konkrét munka
     * („Cookiebot szoftver beüzemelése …") a megjegyzésben. A sor-név szabálya (`assignmentSubLines`) eseti soron
     * egy sorban kiírja.
     */
    invoiceItemNote: z.string().nullable(),
    scope: z.string(),
    type: z.string(),
    status: z.string(),
    quantity: z.string(),
    quantityUnit: z.string().nullable(),
    issuerKey: z.string().nullable(),
    invoiceType: z.string().nullable(),
    invoiceDeliveryType: z.string().nullable(),
    /**
     * A SZÁMLÁZÁS TÍPUSA a hozzárendelés `planSnapshot`-jából (`recurring` | `one_time`), katalógus-fallbackkel.
     *
     * MIÉRT A LISTÁN: az ESETI (`one_time`) sornak nincs számlázási gyakorisága, ezért a „Gyakoriság" cellája
     * em-dash volt — megmagyarázhatatlan üresség egy olyan soron, aminek van ára és van záró dátuma. Élesben
     * **397 ilyen sor** van (mind `tracking` típusú, 396-nak VAN ársora). A cella mostantól a `one_time`
     * jelvényt mutatja helyette: az „eseti" maga a válasz a kérdésre, hogy milyen ciklusban számlázunk.
     */
    billingType: z.string().nullable(),
    billingFrequency: z.string().nullable(),
    billingAgreement: z.string().nullable(),
    billingStartDate: z.string().nullable(),
    billingEndDate: z.string().nullable(),
    contractStartDate: z.string().nullable(),
    contractEndDate: z.string().nullable(),
    /** Számított nettó alap-összeg (Decimal→string; best-effort — a sávos részt a UI jelzi) + deviza. */
    netAmount: z.string().nullable(),
    currency: z.string().nullable(),
    /**
     * ÁRAZÁSI MODELL (`fixed`/`tiered`/`base_tiered`/`commission`/…) — a hozzárendelés `planSnapshot`-jából (az
     * árazás ÁLLAPOTA a hozzárendelés pillanatában), fallbackként a katalógus-plan aktuális értéke.
     *
     * MIÉRT A LISTÁN: a legacy „Árazás" oszlopa az összeg ALATT kiírta, MILYEN modell adja az összeget („fix
     * díjas", „sávos", …) — enélkül egy sávos vagy jutalékos sor nettója megmagyarázhatatlan szám. A
     * kör ezt hozta vissza (badge-ként).
     */
    pricingType: z.string().nullable(),
    /**
     * A hozzárendelés ÁR-SORAI (`service-plan-assignment-prices`) — a lista „Árazás" cellája ezekből olvas.
     *
     * MIÉRT KELL a `netAmount` MELLETT: a számított összeg CSAK az `unit`/`base` sorokból áll össze, tehát egy
     * SÁVOS (`tiered`) vagy JUTALÉKOS hozzárendelésnél `null` — a cellában eddig csak a modell-badge állt, ár
     * nélkül, holott a legacy ott is kiírta a sávokat. A UI a sávokat `címke · ár` sorokként mutatja (mint a
     * katalógus), az elsőt/másodikat, a többit „+N további ársáv" jelzi.
     */
    prices: z.array(z.object({
        /** `unit` | `base` | `tier` | `overflow` | `minimum` | `passive`. */
        type: z.string(),
        tierMin: z.number().nullable(),
        tierMax: z.number().nullable(),
        blockSize: z.number().nullable(),
        /** Decimal→string (soha float). */
        netPrice: z.string(),
        currency: z.string(),
    })),
    /**
     * A HASZNÁLAT FORRÁSA (`usageData.sourceType` nyolc kulcsának egyike), vagy `null`.
     *
     * MIÉRT A LISTÁN IS, nem csak a detailben: a jutalékos/sávos soroknál ez mondja meg, MIT mérünk — ugyanaz a
     * „RoomSome Booking engine / RoomSome (jutalékos)" sor jelenthet direkt foglalást, ajánlatkérést vagy
     * mindkettőt, és a listán a három megkülönböztethetetlen volt (user-lelet). A használati adatok lapja
     * ugyanezt a nyolc kulcsot írja ki, ugyanazzal a felirattal.
     *
     * KIZÁRÓLAG a `planSnapshot`-ból, katalógus-fallback NÉLKÜL (szemben a `pricingType`-pal): a számla-generálás
     * is csak a snapshotot nézi, és forrás nélkül kihagyja a tételt. Egy katalógusból pótolt érték olyan mérést
     * ígérne, amit a generálás nem végez el — a `null` itt INFORMÁCIÓ (élesben 4 jutalékos sor ilyen).
     */
    usageSourceType: z.string().nullable(),
    /**
     * KOMPONENSEK száma (a `parentId`-vel erre a sorra kötött gyerek-hozzárendelések) — 0 = egyszerű.
     * A legacy lista a komplex szolgáltatást KIÍRTA („komplex szolgáltatás"), és ez nem kozmetika: komplex
     * esetben a `netAmount` a KOMPONENSEK összege, nem egy önálló ár. Mérve: 1741 sorból 239 komplex.
     */
    componentCount: z.number(),
    /**
     * A KOMPONENSEK a lista Árazás-cellájának legördülőjéhez (név + mennyiség + egységár) — a legacy lista ugyanígy
     * lenyitva mutatta, mit tartalmaz a komplex szolgáltatás és milyen áron. Egyszerű sornál üres. A `unitPrice`
     * `null`, ha a komponensnek nincs ára (be nem árazott).
     */
    components: z.array(z.object({
        id: z.string(),
        name: z.string().nullable(),
        /** Decimal→string. */
        quantity: z.string(),
        /** Decimal→string (soha float); `null` = nincs beárazva. */
        unitPrice: z.string().nullable(),
        currency: z.string().nullable(),
    })),
    /**
     * ÜGYFÉL-SZINTŰ (`scope='client'`) hozzárendelésnél a KIJELÖLT cél-fiókok (az `assignment-accounts` pivot,
     * névre feloldva, betűrendben) — a legacy ezeket legördülőben mutatta az ügyfél-név alatt. Fiók-szintű
     * sornál üres. Feloldatlan/jog nélküli fiók-név → a lista nem tartalmazza (nem szivárog nyers id).
     */
    targetAccounts: z.array(AccountNamedRefSchema),
    note: z.string().nullable(),
    version: z.number(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
/**
 * Egy KOMPONENS-fa gyerek a detail-ben (a szerkesztő-form komponens-listája): a gyerek id + KATALÓGUS-plan + a
 * feloldott szolgáltatás/plan-név (a snapshotból) + mennyiség + a `unit` ár (Decimal-string) + deviza + OCC-version.
 */
export const ServiceAssignmentChildSchema = z.object({
    id: z.string(),
    servicePlanId: z.string(),
    serviceId: z.string().nullable(),
    serviceName: z.string().nullable(),
    planName: z.string().nullable(),
    quantity: z.string(),
    unitPrice: z.string().nullable(),
    currency: z.string().nullable(),
    /** A GYEREK saját kapcsolattartója/szakértője (pivot, role szerint) — a szerkesztő ebből hidratál. */
    contactId: z.string().nullable(),
    expertId: z.string().nullable(),
    version: z.number(),
});
/** Assignment-detail: a sor + a planSnapshot mezői + az ár-sorai + a „számlázott" jelző (lock/delete-guard). */
export const ServiceAssignmentDetailSchema = ServiceAssignmentRowSchema.extend({
    pricingType: z.string().nullable(),
    billingType: z.string().nullable(),
    vatRate: z.string().nullable(),
    usageSourceType: z.string().nullable(),
    invoiceItemName: z.string().nullable(),
    invoiceItemNote: z.string().nullable(),
    mainCategory: z.string().nullable(),
    subCategory: z.string().nullable(),
    paymentCardId: z.string().nullable(),
    firstInvoiceProrated: z.boolean(),
    showComponentsOnInvoice: z.boolean(),
    prices: z.array(ServiceAssignmentPriceSchema),
    /** A KATALÓGUS-plan pricingType-ja (a down-conversion korlát forrása: az override CSAK ez VAGY `flat` lehet). */
    catalogPricingType: z.string().nullable(),
    /** A pivot-sorokból feloldva: kapcsolattartó / szakértő identity + a client-scope érintett fiókok. */
    contactId: z.string().nullable(),
    expertId: z.string().nullable(),
    accountIds: z.array(z.string()),
    /**
     * A mért azonosítók kötése. `null` = még NINCS kötve (a bevezetés előtti sor) — a generátor ilyenkor a régi
     * szabállyal (a fiók azonosítójából) számol, és ezt naplózza; az űrlap mentése a kötést kötelezően megadja.
     */
    usageRefs: z.array(UsageRefSchema).nullable(),
    /** A megosztott használat kapcsolója (a regisztráltak utáni díjrész a társak között oszlik). */
    splitSharedUsage: z.boolean(),
    /** Van-e rá hivatkozó számla-tétel (best-effort). true → szerkesztés-lock + törlés-tiltás. */
    invoiced: z.boolean(),
    /** A KOMPONENS-fa gyerekei (komplex/csomag hozzárendelés). Üres → egyszerű (nem-komplex) hozzárendelés. */
    children: z.array(ServiceAssignmentChildSchema),
});
/** Assignment lista-lekérdezés: szabad-szöveg + ügyfél/fiók/típus/státusz/gyakoriság-szűrő + offset-lapozás. */
/**
 * A „SZÁMLÁNKÉNT" NÉZET (ügyfél / fiók adatlap) — melyik hozzárendelés melyik leendő számlára kerül. PONTOSAN egy
 * kell a kettőből (`clientId` · `accountId`); a fiók-nézet az ügyfél-szintű, a fiókot érintő hozzárendeléseket is
 * mutatja (a pivot szerint), mert azok is rá számlázódnak.
 */
export const ServiceAssignmentInvoiceGroupsQuerySchema = z.object({
    clientId: z.string().uuid().optional(),
    accountId: z.string().uuid().optional(),
});
/** Miért NEM kerül egy hozzárendelés számlára (a generátor kapuival egyező besorolás). */
export const INVOICE_GROUP_EXCLUSION_REASONS = [
    'tracking',
    'one_time',
    'ended',
    'inactive',
    'incomplete',
];
/** Egy leendő számla: a fejléc (a generátor csoport-kulcsának elemei) + a rá kerülő hozzárendelések. */
export const ServiceAssignmentInvoiceGroupSchema = z.object({
    /** A generátor csoport-kulcsa (`invoice-group.ts`) — stabil azonosító a felületnek. */
    key: z.string(),
    /** Kinek szól: az ügyfél közös számlája, vagy a fiók saját (eltérített) számlája. */
    recipient: z.enum(['client', 'account']),
    recipientName: z.string().nullable(),
    /** Eltérített számlánál a fiók; közösnél `null`. */
    accountId: z.string().nullable(),
    /** Az effektív számlázási profil neve (`billingName`). */
    profileName: z.string().nullable(),
    issuerKey: z.string(),
    invoiceType: z.string(),
    deliveryType: z.string(),
    agreement: z.string(),
    currency: z.string(),
    vatRate: z.string(),
    /** A kibocsátó nincs a kibocsátó-törzsben (pl. `none`) — a generálás az egész számlát kihagyja. */
    issuerMissing: z.boolean(),
    /** A számlára kerülő hozzárendelések (a fiók-nézetben csak a fiókot érintők). */
    assignmentIds: z.array(z.string()),
    /** Ezeknél a tulajdonos (ügyfél / fiók) automata-számlázása ki van kapcsolva — a generálás kihagyja őket. */
    autoBillingOffIds: z.array(z.string()),
    /** A fiók-nézetben: ugyanezen a számlán hány MÁS fiókot érintő hozzárendelés van még (ügyfél-nézetben 0). */
    otherCount: z.number().int(),
});
export const ServiceAssignmentInvoiceGroupsSchema = z.object({
    groups: z.array(ServiceAssignmentInvoiceGroupSchema),
    excluded: z.array(z.object({
        assignmentId: z.string(),
        reason: z.enum(INVOICE_GROUP_EXCLUSION_REASONS),
    })),
});
export const ServiceAssignmentListQuerySchema = z.object({
    q: z.string().optional(),
    clientId: z.string().uuid().optional(),
    accountId: z.string().uuid().optional(),
    /** HATÓKÖR-szűrő (`client` | `account`) — a lista első szűrője (user-döntés). */
    scope: ServiceAssignmentScopeSchema.optional(),
    type: ServiceAssignmentTypeSchema.optional(),
    status: ServiceAssignmentStatusSchema.optional(),
    billingFrequency: ServiceAssignmentBillingFrequencySchema.optional(),
    /** FIZETÉSI MEGÁLLAPODÁS (előre/utólag) — a gyakoriság-szűrő párja. */
    billingAgreement: z.enum(SERVICE_ASSIGNMENT_BILLING_AGREEMENTS).optional(),
    /** ÁRAZÁSI MODELL (fix díjas / sávos / alapdíj+sávos / jutalékos) — a státusz ELŐTTI szűrő. */
    pricingType: PricingTypeSchema.optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/** A form forrásai: katalógus szolgáltatás + plan + plan-ár (a snapshot/ár-forrás), ügyfelek, fiókok. */
export const ServiceAssignmentRefsSchema = z.object({
    services: z.array(z.object({
        id: z.string(),
        name: z.string(),
        structureType: z.string().nullable(),
        status: z.string().nullable(),
    })),
    plans: z.array(z.object({
        id: z.string(),
        serviceId: z.string(),
        name: z.string(),
        mainCategory: z.string().nullable(),
        subCategory: z.string().nullable(),
        billingType: z.string().nullable(),
        pricingType: z.string().nullable(),
        vatRate: z.string().nullable(),
        usageSourceType: z.string().nullable(),
        invoiceItemName: z.string().nullable(),
        invoiceItemNote: z.string().nullable(),
    })),
    planPrices: z.array(z.object({
        id: z.string(),
        planId: z.string(),
        type: z.string(),
        tierMin: z.number().nullable(),
        tierMax: z.number().nullable(),
        blockSize: z.number().nullable(),
        netPrice: z.string(),
        currency: z.string(),
    })),
    /** `status` az owner-státusz-szűkítés forrása a create-form státusz-választójában (null → mind engedélyezett). */
    clients: z.array(z.object({ id: z.string(), name: z.string(), status: z.string().nullable() })),
    /**
     * A fiókok, a külső rendszer-azonosítóikkal (a mért-azonosító választó forrása). Az `integrations` üres, ha
     * a fiók-profilok nem olvashatók (degradálás — a választó ilyenkor jelzi, hogy nincs választható azonosító).
     */
    accounts: z.array(z.object({
        id: z.string(),
        name: z.string(),
        status: z.string(),
        clientId: z.string(),
        integrations: z.array(AccountIntegrationSchema),
    })),
    /** A kliens bankkártyái (a fizetőkártya-választó; a UI a kiválasztott clientId-re szűr). */
    paymentCards: z.array(z.object({
        id: z.string(),
        clientId: z.string(),
        holderName: z.string(),
        brand: z.string(),
        last4: z.string(),
    })),
    /**
     * Személyek a kapcsolattartó/szakértő-választóhoz.
     *
     * A `type` NEM dekoráció: a két mező KÜLÖNBÖZŐ halmazból választ — a **szakértő** a cég belső munkatársa
     * (`internal`), a **kapcsolattartó** az ügyfél-oldali külsős (`partner`). Egy közös, szűretlen listából a
     * felhasználó bármikor a rossz oldalról választhatna (és ezt semmi nem jelezné), ezért a szűrés a UI-ban a
     * `type`-ra épül. (Adat-szinten a pivot mindkettőt engedi — ez a fal a helyes kitöltésé, nem az izolációé.)
     *
     * A `name` a HÍVÓ NYELVÉN összeállított teljes név (`?lang=`): magyarul `Vezetéknév Keresztnév`, angolul
     * `Given Family`. A sorrend a szerveren dől el, hogy a névsor-rendezés ugyanazon a stringen menjen, amit a
     * felhasználó lát — kliens-oldali összefűzéssel a rendezés és a megjelenítés elcsúszhatna.
     */
    identities: z.array(z.object({
        id: z.string(),
        name: z.string(),
        type: z.enum(['internal', 'partner', 'service']),
    })),
    /**
     * KAPCSOLATTARTÓ-KÖTÉSEK (`clients/account-affiliations`, `kind='contact'`): melyik személy melyik FIÓK
     * kapcsolattartója.
     *
     * MIÉRT KELL A FORMNAK: a kapcsolattartó-választó korábban a teljes partner-névtárat kínálta (mérve
     * élesben: 486 ember), miközben egy fiókhoz átlagosan 2 kapcsolattartó tartozik — a felhasználó bármikor
     * választhatott MÁSIK ÜGYFÉL emberét, és ezt semmi nem jelezte (mérve: 343 hozzárendelés-kapcsolattartóból
     * 12 ilyen). A választó ebből a kötés-listából szűkít a CÉL-ÜGYFÉL fiókjaira (user-döntés: ügyfél-szint,
     * nem fiók-szint — egy ember több fióknál is lehet kapcsolattartó, a listában akkor is EGYSZER szerepel).
     *
     * Lapos pár-lista (nem fiókonkénti csoport), mert a form fordítva kérdez: „ez a személy választható-e".
     */
    contactAffiliations: z.array(z.object({ identityId: z.string(), accountId: z.string() })),
});
/**
 * Aktív-hozzárendelés ELLENŐRZÉS (nem-blokkoló figyelmeztetés — a legacy checkForActiveAssignment 1:1): van-e
 * már AKTÍV (nem-lezárt, nem-törölt) hozzárendelés UGYANARRA a szolgáltatásra (a `servicePlanId`-ből feloldott
 * serviceId) UGYANANNÁL a tulajdonosnál (client/account). one_time plannél a BFF kihagyja (abból bármennyi
 * felvehető → active:false). `excludeId` = szerkesztésnél a jelenlegi sort kizárja. A cél-tenant a `clientId`.
 *
 * A `componentServicePlanIds` a KOMPLEX szolgáltatás komponens-planjai a MÉG NEM MENTETT formból: a legacy a
 * fő szolgáltatás MELLETT a komponensek szolgáltatásait is ellenőrizte (`collectServiceIdsToCheck`), és a
 * keresés a meglévő sorok közt is kitért a komponensekre (`parent_id IS NOT NULL`) — különben egy komponensként
 * már meglévő szolgáltatás újra felvehető észrevétlenül.
 */
export const ServiceAssignmentActiveCheckQuerySchema = z.object({
    servicePlanId: z.string().uuid(),
    clientId: z.string().uuid(),
    accountId: z.string().uuid().optional(),
    scope: ServiceAssignmentScopeSchema,
    excludeId: z.string().uuid().optional(),
    /**
     * A form komponens-planjai (komplex szolgáltatásnál). Query-stringben ismételt paraméter
     * (`componentServicePlanIds=…&componentServicePlanIds=…`); egyetlen érték is tömbre normalizálódik.
     */
    componentServicePlanIds: z.preprocess((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]), z.array(z.string().uuid()).max(50).optional()),
});
/** Aktív-hozzárendelés ellenőrzés eredménye: van-e ütköző aktív hozzárendelés + a legelső találat (a UI a
 *  „Meglévő megtekintése" linkhez / a figyelmeztető szöveghez használja). */
export const ServiceAssignmentActiveCheckSchema = z.object({
    active: z.boolean(),
    existing: z
        .object({
        id: z.string(),
        serviceName: z.string().nullable(),
        ownerName: z.string().nullable(),
        /**
         * A találat HELYE: `root` = önálló hozzárendelés, `component` = egy másik hozzárendelés komponense
         * (ilyenkor az `id` a SZÜLŐ hozzárendelés id-je, hogy a „Meglévő megtekintése" oda navigáljon).
         */
        matchedIn: z.enum(['root', 'component']),
        /** A form melyik eleme ütközött: `service` = a fő szolgáltatás, `component` = egy komponens. */
        matchedFrom: z.enum(['service', 'component']),
    })
        .nullable(),
});
/**
 * Egy FÜGGŐBEN LÉVŐ (`pending`) hozzárendelés, amelynek az induló dátuma N napon belül van — „ellenőrizendő":
 * ha addig nem kerül `active`-ra, a havi generátor kihagyja. A `startDate` KIZÁRÓLAG a `billingStartDate`
 * (`YYYY-MM-DD`; a szerződés kezdete nem számít); a `daysUntilStart` a BFF budapesti „ma"-jához mért nap
 * (0 = ma indul, negatív = már el kellett volna indulnia, de még függő).
 */
export const ServiceAssignmentPendingStartSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientName: z.string().nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    serviceName: z.string().nullable(),
    planName: z.string().nullable(),
    startDate: z.string(),
    daysUntilStart: z.number().int(),
});
/**
 * A hozzárendelés-lista FIGYELEM-panelje: a hamarosan (N napon belül) induló függő hozzárendelések + az N
 * (`noticeDays`, a BFF konfigurációja — a UI innen olvassa, nem magic number). Ugyanez a szabály hajtja a
 * napi értesítő cront (`service_assignment.pending_start_soon`).
 */
export const ServiceAssignmentAttentionSchema = z.object({
    noticeDays: z.number().int().positive(),
    pendingStartingSoon: z.array(ServiceAssignmentPendingStartSchema),
});
/**
 * Ha nincs `amount`, de a hiány OKA kimondható (a sor és a bontás egy-egy eleme is ezt hordozza):
 *  • `no_rate`          — kellene kereszt-deviza árfolyam a hónapra, de nincs felvéve (a számlából is kimaradna),
 *  • `aggregated`       — a hozzárendelés több külső azonosítót összegez, tehát a SÁVOS ár a teljes mennyiségé;
 *                         egy sorra arányosítani kitalált szám lenne,
 *  • `not_due`          — ebből a hónapból NEM lesz tétel (a hónap a szerződés-időszakon kívül esik),
 *  • `auto_billing_off` — az automata számlázás KAPCSOLÓJA ki van kapcsolva (ügyfél-scope-on az ügyfélé,
 *                         fiók-scope-on a fióké), tehát a generátor sem állítana ki tételt,
 *  • `owner_inactive`   — a tulajdonos ügyfél/fiók CRM-státusza nem `active`. ⚠️ KÜLÖN ok a kapcsolótól,
 *                         ugyanazzal a bontással, mint a generátor kihagyás-naplójában: MÁS A TEENDŐ.
 * Minden más esetben (nincs hozzárendelés, jutalékos sor, darab-alapú `flat` ár) `null`.
 *
 * _(Korábban volt egy `ambiguous` ok is — „több hozzárendelés fogyasztja" —, összeg nélkül. Helyette a sor
 * hozzárendelésenkénti BONTÁST ad (`consumers`): több hotel ugyanazon a programon a normál eset, nem hiba.)_
 */
export const UsageAmountNoteSchema = z.enum([
    'no_rate',
    'aggregated',
    'not_due',
    'auto_billing_off',
    'owner_inactive',
]);
/** A megosztott használat adatai egy összegen: a TELJES regisztrált utáni díj és a társak száma. */
export const UsageAmountSharedSchema = z.object({
    total: z.string(),
    count: z.number().int(),
});
/** Egy hozzárendelés része egy több hozzárendelés által fogyasztott mérésből (a sor bontása). */
export const UsageConsumerAmountSchema = z.object({
    assignmentId: z.string(),
    /** A hozzárendelés gazdája a felületen: fiók-szintűnél a fiók, ügyfél-szintűnél az ügyfél neve. */
    ownerName: z.string().nullable(),
    /** A számla-tétel neve (a pillanatkép `invoiceItemName`-je, ennek híján a csomag neve). */
    planName: z.string().nullable(),
    amount: z.string().nullable(),
    amountBase: z.string().nullable(),
    amountCurrency: z.string().nullable(),
    amountNote: UsageAmountNoteSchema.nullable(),
    amountShared: UsageAmountSharedSchema.nullable(),
    amountFrequency: z.enum(['monthly', 'quarterly', 'semi_annual', 'annual']).nullable(),
    amountPeriodStart: z.string().nullable(),
    amountPeriodEnd: z.string().nullable(),
    amountProrated: z.boolean(),
    /** Be van-e kapcsolva rajta a megosztott használat. */
    splitSharedUsage: z.boolean(),
});
/** Egy service-usage sor + a best-effort feloldott fiók-hivatkozás(ok) (a külső ID → fiók-nevek). */
export const ServiceUsageRowSchema = z.object({
    id: z.string(),
    usageSourceType: z.string(),
    sourceReferenceId: z.string(),
    year: z.number(),
    month: z.number(),
    /** Nyers MÉRT ÉRTÉK — Decimal-string (soha float). Hiány → null. Hogy MIT mér, a forrás-típus dönti el. */
    usageQuantity: z.string().nullable(),
    /**
     * A mért érték DEVIZÁJA — kizárólag JUTALÉKOS soron (ott a mért érték már pénz, a `raw_data.currency`-ből);
     * hírlevél/regisztráció soron `null`, mert ott az érték darab, illetve fő.
     */
    quantityCurrency: z.string().nullable(),
    /**
     * A mért mennyiségből számolt nettó összeg (Decimal-string) — PONTOSAN az a tétel-nettó, amit a hónap
     * számla-generálása kiállít: ugyanaz a motor, ugyanaz az esedékesség-, arányosítás- és kerekítés-szabály
     * (`usage-data/usage-row-amount.ts` fejléce sorolja a hat lépést). Csak NEM-jutalékos soron (ott a mért
     * érték már az összeg), és csak akkor, ha a sor egyértelműen EGY hozzárendeléshez tartozik.
     */
    amount: z.string().nullable(),
    amountCurrency: z.string().nullable(),
    /**
     * Ha nincs `amount`, de a hiány OKA kimondható:
     *  • `no_rate`          — kellene kereszt-deviza árfolyam a hónapra, de nincs felvéve (a számlából is kimaradna),
     *  • `aggregated`       — a hozzárendelés több külső azonosítót összegez, tehát a SÁVOS ár a teljes mennyiségé;
     *                         egy sorra arányosítani kitalált szám lenne,
     *  • `not_due`          — ebből a hónapból NEM lesz tétel: nem-havi gyakoriságnál a periódusnak csak az
     *                         ELSŐ hónapja megy számlára (a többi hónap adata abban az egy tételben van), vagy
     *                         a hónap a szerződés-időszakon kívül esik,
     *  • `auto_billing_off` — az automata számlázás KAPCSOLÓJA ki van kapcsolva (ügyfél-scope-on az ügyfélé,
     *                         fiók-scope-on a fióké), tehát a generátor sem állítana ki tételt,
     *  • `owner_inactive`   — a tulajdonos ügyfél/fiók CRM-státusza nem `active`. ⚠️ KÜLÖN ok a kapcsolótól,
     *                         ugyanazzal a bontással, mint a generátor kihagyás-naplójában: MÁS A TEENDŐ (a
     *                         kapcsolót vissza lehet kapcsolni; az inaktív tulajdonosnál a sort kellett volna
     *                         lezárni). Amíg a kettő egy néven futott, a lap és a napló két külön magyarázatot
     *                         adott volna ugyanarra a helyzetre.
     * Minden más esetben (nincs hozzárendelés, jutalékos sor, darab-alapú `flat` ár) `null`.
     */
    amountNote: UsageAmountNoteSchema.nullable(),
    /**
     * ALAPDÍJ + SÁVOS (`base_tiered`) soron az ALAPDÍJ része (a sávdíjjal, arányosítva) — a bizonylat ELSŐ tétele.
     * Az `amount` ilyenkor a mérésből számolt rész (a túlfutás, illetve megosztott használatnál a társra jutó
     * regisztráltak utáni díj) — a bizonylat MÁSODIK tétele. A kettő összege a hónap teljes díja. Más árazásnál `null`.
     */
    amountBase: z.string().nullable(),
    /**
     * MEGOSZTOTT HASZNÁLAT: a regisztráltak utáni TELJES díj (`total`) ennyi társ (`count`) között oszlik — az
     * `amount` ebből az erre a hozzárendelésre jutó rész. `null` → nincs osztás.
     */
    amountShared: UsageAmountSharedSchema.nullable(),
    /**
     * HOZZÁRENDELÉSENKÉNTI BONTÁS, ha TÖBB hozzárendelés is fogyasztja ezt az azonosítót (pl. több hotel ugyanazon a
     * loyalty-programon) — mindegyik a saját árazásával, a saját számlájára. Ilyenkor a sor-szintű `amount` üres, és
     * a felület a bontást mutatja. Egy fogyasztónál üres tömb.
     */
    consumers: z.array(UsageConsumerAmountSchema),
    /**
     * Ugyanarra az azonosítóra kötött hozzárendelések közül csak egy RÉSZÉN van bekapcsolva a megosztott használat —
     * a rész így kevesebb felé oszlik, mint ahány hotel a programon van. Figyelmeztetés, nem hiba.
     */
    splitMismatch: z.boolean(),
    /**
     * A SZÁMLÁZOTT IDŐSZAK, amit a kiírt összeg fedez (`YYYY-MM-DD`) — a számla-tétel „tól-ig"-ja. Nem-havi
     * gyakoriságnál ez mondja meg, hogy a mért hónap MELYIK periódust nyitja.
     */
    amountPeriodStart: z.string().nullable(),
    amountPeriodEnd: z.string().nullable(),
    /** Az ELSŐ számla ARÁNYOSÍTOTT tétele (a periódus első hónapjának fele hiányzik) — a felület kiírja. */
    amountProrated: z.boolean(),
    /**
     * A hozzárendelés számlázási GYAKORISÁGA — csak ott, ahol van `amount`. Nem-havi értéknél a kiírt összeg
     * egy TELJES PERIÓDUS díja (a hozzárendelés mennyisége a periódus hónap-száma), és a számla sem havonta
     * állítja ki: a felület ezt kimondja, különben a szám havi díjnak látszana.
     */
    amountFrequency: z.enum(['monthly', 'quarterly', 'semi_annual', 'annual']).nullable(),
    /**
     * MELYIK KÜLSŐ RENDSZER azonosítója a `sourceReferenceId` — a badge felirata ebből lesz „MailWizz ID" /
     * „HotelCMS ID" / „ZadirCMS ID" a korábbi, forrás-vak „ID" helyett. A hírlevél a MailWizz-azonosítón megy,
     * a loyalty-típusok a HotelCMS-en (tartalékban ZadirCMS) — a tényleges választást a lekérés írja a sor
     * `raw_data.selected_source` mezőjébe. Ismeretlen → `null` (a badge ilyenkor a semleges „ID").
     */
    sourceSystem: z.enum(['mailwizz', 'hotelcms', 'zadircms']).nullable(),
    /** A külső ID-hoz feloldott fiók-nevek (best-effort; üres, ha nincs illeszkedő fiók). */
    accountNames: z.array(z.string()),
    /**
     * MEGJEGYZÉS a sorhoz — miért LESZ vagy NEM LESZ ebből a mérésből számla-tétel (a lap „Megjegyzés" oszlopa,
     * a generálási kihagyás mintájára: badge + magyarázó mondat). `null` → nem eldönthető (a hozzárendelés-
     * kontextus olvasása nem sikerült).
     *
     * Az ÉRVÉNYESSÉGET a SOR HÓNAPJÁRA a hozzárendelés kezdő/záró dátuma dönti el (nem a mai státusza); az
     * inaktivitás csak megjegyzés (user-döntés):
     *
     *   `billed`            — a hónapban érvényes hozzárendelés fogyasztja ezt a forrás-típust ezen az azonosítón, és
     *                          számlázunk belőle. Ha a sor azóta INAKTÍV, de LEZÁRT (záró dátummal), az `inactive` megmondja,
     *                          mi inaktív, a `date` a záró nap — a záró dátumig számlázunk (a generátorral azonos szabály);
     *   `billed_inactive`   — van a hónapban érvényes hozzárendelés, de a szolgáltatás / a fiók / az ügyfél inaktív záró
     *                          dátum NÉLKÜL, vagy a hozzárendelés függő (`inactive` mondja meg, melyik) — nem készül belőle számla;
     *   `other_variant`     — jutaléknál a szálláshely szerződése a MÁSIK változatot használja (ág-bontás ↔
     *                          modul-végösszeg; `usedSourceType` mondja meg, melyiket) — ez a sor csak tájékoztató;
     *   `ended`             — volt ilyen hozzárendelés, de a hónap ELŐTT lezárult (`date` = az utolsó nap);
     *   `not_started`       — van ilyen hozzárendelés, de a hónap UTÁN indul (`date` = az első nap);
     *   `no_assignment`     — van fiók, nincs ilyen típusú hozzárendelése, és VAN mért érték (lehet, hogy számlázni kellene);
     *   `unused`            — van fiók, nincs ilyen típusú hozzárendelése, a mérés 0 (nem igénybe vett szolgáltatás, nem hiány);
     *   `no_account`        — egyik fiókon sincs ez az azonosító, és van mért érték (az adat nem köthető ügyfélhez);
     *   `no_account_unused` — egyik fiókon sincs ez az azonosító, a mérés 0;
     *   `tracking`          — van a hónapban érvényes hozzárendelés erre az azonosítóra, de NYILVÁNTARTÁSOS (nem számlázunk belőle);
     *   `bound_elsewhere`   — a fiókjáé az azonosító, de a hónapban érvényes, ilyen típusú hozzárendelése egy MÁSIK
     *                          azonosítóra van kötve (a kötést kell ellenőrizni — ebből a sorból nem lesz tétel);
     *   `not_final` / `disabled` — jutalék ÁLLAPOT-SOR: a HotelCMS-ben még nincs lezárva / ki van kapcsolva;
     *   `combined_minimum`  — jutalék ÁLLAPOT-SOR: ágankénti RoomSome-típus, de a szálláshely ÖSSZEVONT minimummal zár
     *                          (a nyers ág-összeg nem számlázható — Direkt + AK hozzárendelés kell).
     *
     * (Korábban egy `billed: boolean` volt, a felületen „nem ebből számlázunk" felirattal — user-lelet: nem
     * derült ki belőle, MIÉRT nem. Utána a döntés a hozzárendelés MAI státuszát nézte, a hónapot és a mért
     * értéket nem — user-lelet: a csak jutalékos szálláshelyen a 0-s Loyalty-sor is „hiányzott".)
     */
    billingNote: z
        .object({
        code: z.enum([
            'billed',
            'billed_inactive',
            'other_variant',
            'tracking',
            'bound_elsewhere',
            'ended',
            'not_started',
            'no_assignment',
            'unused',
            'no_account',
            'no_account_unused',
            'not_final',
            'disabled',
            'combined_minimum',
        ]),
        usedSourceType: z.string().nullable(),
        /** `ended` / lezárt `billed`: a záró nap · `not_started`: az első (`YYYY-MM-DD`). Egyébként `null`. */
        date: z.string().nullable(),
        /** `billed_inactive` / azóta inaktív, lezárt `billed`: mi nem aktív (`pending` = függő hozzárendelés). */
        inactive: z.enum(['assignment', 'pending', 'account', 'client']).nullable(),
    })
        .nullable(),
    /**
     * A KOMBINÁLT RoomSome-sor (`rs_direct_referral`) ÁG-BONTÁSA — a két ág mért jutaléka és foglalás-száma
     * a riportból. CSAK azon a soron, és csak akkor, ha a szálláshelyre a MODUL-VÉGÖSSZEGRE van szerződés
     * (akkor a lap EZT az egy sort mutatja a két ág sora helyett).
     *
     * MIÉRT A SORON BELÜL: a kombinált összeg a két ág ÖSSZEGE, és a felhasználónak látnia kell, miből áll —
     * de három külön sorként ugyanaz a pénz háromszor jelent meg, és összeadhatónak látszott.
     */
    commissionBranches: z
        .object({
        direct: z.object({ amount: z.string(), bookings: z.number() }).nullable(),
        inquiry: z.object({ amount: z.string(), bookings: z.number() }).nullable(),
    })
        .nullable(),
});
/**
 * A HAVI USAGE-LEKÉRÉS kérése (a legacy `usage:fetch` command Atlas-oldali párja). A hónap kötelező; a
 * `sourceTypes` opcionális szűkítés (üres/hiányzó → MIND A NYOLC implementált típus: a három
 * hírlevél/loyalty forrás — newsletter, loyalty_newsletter, rs_loyalty (`usage-fetch.util.ts`) — plusz az
 * öt jutalék-típus — rs_direct, rs_referral, rs_direct_referral, rs_voucher, ticket
 * (`commission.util.ts`), amiknek a HotelCMS jutalék-riportja a valódi adatforrása).
 */
export const ServiceUsageFetchBodySchema = z.object({
    year: z.number().int().min(2000).max(2100),
    month: z.number().int().min(1).max(12),
    sourceTypes: z.array(UsageSourceTypeSchema).optional(),
});
/**
 * A lekérés eredménye. `written` = ahány sor létrejött vagy frissült; `perType` a típusonkénti bontás;
 * `skipped` a KIHAGYOTT típusok gépi kódja + oka (nincs adatforrás, nincs konfigurálva, upstream-hiba, vagy
 * a kért hónapra nem értelmezett) — a UI ezt lokalizálja, tehát a szerver nem küld emberi szöveget.
 *
 * `period_unsupported`: a loyalty-riportoknak (`loyalty_newsletter`, `rs_loyalty`) NINCS periódus-paramétere
 * — a forrás a MAI állapotot adja. Egy régi hónapra kért lekérés tehát a mai számot írná rá, felülírva a
 * tényleges historikus értéket; ezért a szerver ezt a két típust csak az AKTUÁLIS vagy az ELŐZŐ hónapra
 * futtatja, régebbire kihagyja ezzel az okkal (ha a hívó KIFEJEZETTEN kérte, 422-t kap).
 */
export const ServiceUsageFetchResultSchema = z.object({
    year: z.number(),
    month: z.number(),
    written: z.number(),
    created: z.number(),
    updated: z.number(),
    /**
     * Hány sor ÍRÁSA bukott el (fojtás a lépcsők után, OCC-ütközés, séma-hiba) — ÚJRAPRÓBÁLHATÓ.
     *
     * Amíg nem volt a válaszban, egy bukott köteg így nézett ki a felületen: „200 · 12 sor", miközben sorok
     * százai nem íródtak ki, és a `perType.written` is csak 0-t mutatott, ok nélkül.
     */
    failed: z.number(),
    perType: z.array(z.object({ sourceType: z.string(), written: z.number() })),
    skipped: z.array(z.object({
        sourceType: z.string(),
        reason: z.enum(['no_source_api', 'not_configured', 'upstream_error', 'period_unsupported']),
    })),
});
/** A UsageDataChecker lista-szűrője: év/hó (a havi keresés kulcsa) + típus + szabad-szöveg + lapozás. */
export const ServiceUsageListQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
    month: z.coerce.number().int().min(1).max(12),
    /** Típus-szűrő (MULTI, #52): ismételt query-param VAGY egyetlen érték; hiány / üres → mind. */
    sourceType: z.preprocess(toQueryArray, z.array(UsageSourceTypeSchema)).optional(),
    /**
     * HOZZÁRENDELÉS-SZŰRŐ: `missing` → csak azok a MÉRT sorok, amelyekből ma nem lesz számla, mert nincs mihez kötni
     * (az azonosító egyik fiókon sincs, vagy van fiók, de nincs ilyen típusú hozzárendelés). A lekérés minden
     * visszaadott sort eltárol — ez a szűrő mutatja meg, hol hiányzik a besorolás.
     */
    assignment: z.enum(['missing']).optional(),
    q: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
//# sourceMappingURL=schemas.js.map